import { useCallback, useEffect, useRef } from 'react';
import { useAuth } from './useAuth';

const MIN_PING_INTERVAL_MS = 5 * 60 * 1000;
const ACTIVITY_DEBOUNCE_MS = 1000;

const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'] as const;

const useSessionKeepalive = (enabled: boolean) => {
    const { mutate } = useAuth();
    const lastPingAtRef = useRef(0);
    const debounceTimerRef = useRef<number>();

    const pingSession = useCallback(async () => {
        const now = Date.now();
        if (now - lastPingAtRef.current < MIN_PING_INTERVAL_MS) {
            return;
        }

        lastPingAtRef.current = now;

        try {
            await mutate();
        } catch {
            // Expired sessions are handled by the global 401 interceptor and route guard.
        }
    }, [mutate]);

    useEffect(() => {
        if (!enabled) {
            return;
        }

        const schedulePing = () => {
            window.clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = window.setTimeout(() => {
                void pingSession();
            }, ACTIVITY_DEBOUNCE_MS);
        };

        ACTIVITY_EVENTS.forEach((eventName) => {
            window.addEventListener(eventName, schedulePing, { passive: true });
        });

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                schedulePing();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', schedulePing);

        return () => {
            ACTIVITY_EVENTS.forEach((eventName) => {
                window.removeEventListener(eventName, schedulePing);
            });
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', schedulePing);
            window.clearTimeout(debounceTimerRef.current);
        };
    }, [enabled, pingSession]);
};

export { useSessionKeepalive };
